import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Alert,
  Box,
  LinearProgress,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatNumber, formatKstDateTime } from '../../utils/formatUtils';
import { PageHeader, StatCard, ClientChip } from '../../components/common';

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/dashboard');
      setData(res.data);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const u = data?.users || {};
  const s = data?.subscriptions || {};

  return (
    <>
      <PageHeader
        title="대시보드"
        description="회원·접속·구독 현황 (1분마다 갱신)"
        actions={
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            새로고침
          </Button>
        }
      />
      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="현재 접속 회원"
            value={formatNumber(data?.online?.users)}
            sub={`활성 세션 ${formatNumber(data?.online?.sessions)}개`}
            color="success.main"
            onClick={() => navigate('/admin/online')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="접속 중 에이전트"
            value={formatNumber(data?.online?.agents)}
            sub={`최근 ${data?.agentOnlineMinutes ?? '-'}분 내 동기화`}
            color="success.main"
            onClick={() => navigate('/admin/agents')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="유료 구독 회원"
            value={formatNumber(s.active_users)}
            sub={`7일 내 만료 ${formatNumber(s.expiring_7d)}건`}
            color="primary.main"
            onClick={() => navigate('/admin/subscriptions')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="오늘 로그인 실패"
            value={formatNumber(data?.loginFailuresToday)}
            sub="비밀번호 오류·미인증·정지 계정 등"
            color={data?.loginFailuresToday > 20 ? 'error.main' : undefined}
            onClick={() => navigate('/admin/history')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="전체 회원" value={formatNumber(u.total)} sub={`정상 ${formatNumber(u.active)} · 정지 ${formatNumber(u.suspended)} · 탈퇴 ${formatNumber(u.withdrawn)}`} onClick={() => navigate('/admin/users')} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="신규 가입" value={formatNumber(u.joined_today)} sub={`최근 7일 ${formatNumber(u.joined_7d)}명`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="24시간 내 로그인" value={formatNumber(u.login_24h)} sub={`이메일 미인증 ${formatNumber(u.unverified)}명`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="최근 30일 구독 결제" value={`${formatNumber(s.revenue_30d)}원`} sub={`신규·갱신 ${formatNumber(s.started_30d)}건`} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              요금제별 활성 구독
            </Typography>
            {(s.byPlan || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                활성 구독이 없습니다.
              </Typography>
            )}
            {(s.byPlan || []).map((p) => (
              <Box key={p.code} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography>
                  {p.name} <Chip size="small" label={p.billingCycleLabel} sx={{ ml: 0.5 }} />
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{formatNumber(p.count)}명</Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                최근 로그인 기록
              </Typography>
              <Button size="small" onClick={() => navigate('/admin/history')}>
                전체 보기
              </Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>시각</TableCell>
                  <TableCell>회원</TableCell>
                  <TableCell>구분</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>결과</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.recentLogins || []).map((h) => (
                  <TableRow
                    key={h.id}
                    hover
                    sx={{ cursor: h.userId ? 'pointer' : 'default' }}
                    onClick={() => h.userId && navigate(`/admin/users/${encodeURIComponent(h.userId)}`)}
                  >
                    <TableCell>{formatKstDateTime(h.createdAt)}</TableCell>
                    <TableCell>{h.username || h.email || '-'}</TableCell>
                    <TableCell>
                      {h.action} <ClientChip client={h.client} />
                    </TableCell>
                    <TableCell>{h.ipAddress}</TableCell>
                    <TableCell>
                      <Chip size="small" label={h.success ? '성공' : h.message || '실패'} color={h.success ? 'success' : 'error'} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard;
